import React, { useEffect, useState } from "react";
import { Trophy } from "lucide-react";
import { getCourses, getCoursesSync, getContent, getLocalContent } from "../api";
import { TopRatedCard } from "../components/CourseCard";
import SEO from "../components/SEO";
import { buildCollectionSchema, buildBreadcrumbSchema } from "../utils/seoSchemas";

const PAGE_SIZE = 24;

const TopRated = () => {
  const [all, setAll] = useState(() => getCoursesSync({ sort: "top" }));
  const [content, setContent] = useState(() => getLocalContent());
  const [visible, setVisible] = useState(PAGE_SIZE);

  useEffect(() => {
    getCourses({ sort: "top" }).then((data) => { if (Array.isArray(data)) setAll(data); }).catch(() => {});
    getContent().then((data) => { if (data) setContent(data); }).catch(() => {});
  }, []);

  const pageTitle = content?.topRatedTitle || "Top Rated Mini Golf Courses";
  const pageSubtitle = content?.topRatedSubtitle || `The highest-rated mini golf courses in Alabama and across all 50 states, ranked by real visitor ratings and verified review counts (${all.length} courses total).`;

  const topRatedSchemas = [
    buildCollectionSchema(
      pageTitle,
      pageSubtitle,
      all,
      "https://minigolfalabama.com/top-rated"
    ),
    buildBreadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Top Rated Mini Golf", url: "/top-rated" },
    ]),
  ];

  return (
    <div className="min-h-[60vh] bg-[#f8fafc]" data-testid="top-rated-page">
      <SEO
        title={`${pageTitle} Near Me (${all.length}+ Venues)`}
        description={pageSubtitle.slice(0, 160)}
        canonicalUrl="https://minigolfalabama.com/top-rated"
        ogType="website"
        schema={topRatedSchemas}
      />
      <div className="bg-[#181a20] text-white border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                <Trophy className="w-6 h-6 text-[#f5cd79]" />
              </div>
              <h1 className="text-3xl sm:text-5xl font-black text-white tracking-tight">
                {pageTitle}
              </h1>
            </div>
            <p className="mt-3 text-gray-300 max-w-2xl">
              {pageSubtitle}
            </p>
          </div>
          <a
            href="/courses"
            className="self-start md:self-auto px-5 py-3 rounded-xl bg-[#e5243b] hover:bg-[#cf1c31] text-white font-bold text-sm shadow-md transition-all whitespace-nowrap"
          >
            All Courses Directory ({all.length}) &rarr;
          </a>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {all.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-2xl p-12 text-center max-w-lg mx-auto shadow-sm">
            <Trophy className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-800 mb-2">No Courses Listed Yet</h3>
            <p className="text-gray-500 text-sm">
              No courses have been published yet. Courses added via the Admin Panel will appear here automatically.
            </p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-6 text-sm text-gray-500">
              <div>
                Showing <span className="font-bold text-gray-900">{Math.min(visible, all.length)}</span> of{" "}
                <span className="font-bold text-gray-900">{all.length}</span> top rated courses
              </div>
              {visible < all.length && (
                <button
                  onClick={() => setVisible(all.length)}
                  className="text-xs font-bold text-[#e5243b] hover:underline"
                >
                  Show All ({all.length})
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="top-rated-list">
              {all.slice(0, visible).map((course) => (
                <TopRatedCard key={course.id} course={course} />
              ))}
            </div>
            {visible < all.length && (
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10">
                <button
                  onClick={() => setVisible((v) => v + PAGE_SIZE)}
                  data-testid="load-more-button"
                  className="px-6 py-3 text-sm font-bold text-white bg-[#e5243b] hover:bg-[#cf1c31] rounded-xl shadow-md transition-colors"
                >
                  Load More Courses ({all.length - visible} remaining)
                </button>
                <button
                  onClick={() => setVisible(all.length)}
                  className="px-6 py-3 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
                >
                  Show All {all.length} Courses
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TopRated;
